import isEmpty from 'lodash/isEmpty';
import React, { Fragment, useContext } from 'react';
import { Box, Column, Copy, Optional } from '@honda-canada/design-system-react';

import ActionLink from '../shared/ActionLink';
import CoverImage from '../shared/CoverImage';
import HeaderContext from '../service/HeaderContext';
import themeStyles from '../styles/Header.styles';
import { MAX_NAV_ITEMS } from '../service/constants';
import getCtaLink from '../service/utils/getCtaLink';

const MegaMenuGroupWrapper = themeStyles.apply(Box, 'MegaMenuGroup');
const MegaMenuGroupRow = themeStyles.apply(Box, 'MegaMenuGroupRow');
const MenuColumnTitle = themeStyles.apply(Copy, 'MenuColumnTitle');

const MegaMenuGroupItem = ({ groupItem, groupLengthMoreOne, index, parentName }) => {
  const { itemAlignment, title, items, itemDisplayName: parentDisplayName } = groupItem;
  const { styles: { megaMenuGroup } = {}, navHeading, navHeadingFont, isFirstColumnBold } = useContext(HeaderContext);
  const rightAligned = itemAlignment?.toLowerCase() === 'right';

  const firstColumn = index === 0 && groupLengthMoreOne;
  // based on config first column is set to be bold or not
  const isFirstColumnAndBold = firstColumn && isFirstColumnBold;

  // This ensures font type when only one (subset) is available
  const isGroupLengthOne = !groupLengthMoreOne && isFirstColumnBold;

  const fontFamily = isFirstColumnAndBold || isGroupLengthOne ? navHeading : navHeadingFont;

  return (
    <Column ml={rightAligned ? 'auto' : undefined} data-testid="header-menu-nav-mega-menu-group-list-group">
      <Optional when={title}>
        <Box mt={0} mb="m" textAlign={itemAlignment}>
          <MenuColumnTitle
            as="span"
            d="inline"
            fontWeight={megaMenuGroup?.header?.fontWeight}
            fontFamily={navHeading}
            size={megaMenuGroup?.copySize}
            data-testid="header-menu-nav-mega-menu-group-list-group-title"
          >
            {title}
          </MenuColumnTitle>
        </Box>
      </Optional>
      {items?.map(item => {
        const { itemId, itemName, image, pillLabel, pillType } = item;
        const ctaLink = getCtaLink(item);
        if (!ctaLink) return null;

        const hasItemImage = !isEmpty(image?.src);
        return (
          <Fragment key={itemId}>
            <Optional when={!hasItemImage}>
              <Box mt={0} mb="m" textAlign={itemAlignment}>
                <ActionLink
                  data-testid="header-menu-nav-mega-menu-group-list-group-list-item"
                  copyFontWeight={firstColumn ? megaMenuGroup?.firstColumn?.fontWeight : 'regular'}
                  copySize={megaMenuGroup?.copySize}
                  fontFamily={fontFamily}
                  parentDisplayName={parentDisplayName}
                  pill={{ pillLabel, pillType }}
                  gtmTitle={ctaLink?.text}
                  gtmParentName={parentName}
                  gtmItemName={itemName}
                  gtmTags={{ type: 'header' }}
                  {...ctaLink}
                />
              </Box>
            </Optional>
            <Optional when={hasItemImage}>
              <Box display="inline-flex" flexDirection="column" ml="default" textAlign={itemAlignment}>
                <CoverImage
                  maxWidth="260px"
                  image={image}
                  mb="m"
                  data-testid="header-menu-nav-mega-menu-group-list-group-item-image"
                />
                <ActionLink
                  data-testid="header-menu-nav-mega-menu-group-list-group-item-label"
                  copySize={megaMenuGroup?.copySize}
                  fontFamily={navHeadingFont}
                  parentDisplayName={parentDisplayName}
                  iconColor="black"
                  gtmParentName={parentName}
                  gtmItemName={itemName}
                  gtmTags={{ type: 'Header' }}
                  withIcon
                  {...ctaLink}
                />
              </Box>
            </Optional>
          </Fragment>
        );
      })}
    </Column>
  );
};

const renderItems = (items, groupLengthMoreOne, parentName) =>
  items
    .slice(0, MAX_NAV_ITEMS)
    .map((groupItem, index) => (
      <MegaMenuGroupItem
        groupItem={groupItem}
        index={index}
        key={groupItem?.itemId}
        groupLengthMoreOne={groupLengthMoreOne}
        parentName={parentName}
      />
    ));

const MegaMenuGroups = ({ currentRef, isOpen, firstOpen, itemParams }) => {
  const { styles: { megaMenuGroup, navItemPrimary } = {} } = useContext(HeaderContext);

  const { items: groupItems, itemName } = itemParams;
  if (!groupItems || !groupItems.length) return null;

  const groupLengthMoreOne = groupItems.length > 1;

  return (
    <MegaMenuGroupWrapper
      data-testid="header-menu-nav-mega-menu-group"
      ref={currentRef}
      isOpen={isOpen}
      firstOpen={firstOpen}
      $height={navItemPrimary?.heightNumber}
      $bg={megaMenuGroup?.backgroundColor}
    >
      <MegaMenuGroupRow
        $bg={megaMenuGroup?.firstColumn?.shadow}
        firstColumnLeftPadding={megaMenuGroup?.firstColumn?.firstColumnLeftPadding}
        columnBgPadding={groupLengthMoreOne && megaMenuGroup?.columnBgPadding}
        singleColumn={!groupLengthMoreOne}
      >
        {renderItems(groupItems, groupLengthMoreOne, itemName)}
      </MegaMenuGroupRow>
    </MegaMenuGroupWrapper>
  );
};

export default MegaMenuGroups;
